class ProductCard {
  constructor(node, ratingData, netflixBadgeUrl = '', promoBadge = null, promoEnabled = false) {
    this.product = node;
    this.ratingAvg = ratingData.avg;
    this.netflixBadgeUrl = netflixBadgeUrl;
    this.promoBadge = promoBadge;
    this.promoEnabled = promoEnabled;
  }

  renderStars() {
    if (this.ratingAvg <= 3.0) return ''; 

    return `
      <div class="opinew-stars-plugin-product-list opinew-style">
        <div class="opinew_review_stars">
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.75928 0.5L4.84186 2.80676L7.26319 3.17677L5.51104 4.97229L5.92485 7.50782L3.75928 6.31067L1.59372 7.50782L2.00753 4.97229L0.255371 3.17677L2.6767 2.80676L3.75928 0.5Z" fill="#FFB900"/>
          </svg>
        </div>
        <div class="opinew_review_stars_rate">${this.ratingAvg.toFixed(1)}</div>
      </div>
    `;
  }
  
  getSaleInfo(product) {
    // Use cents for precise integer arithmetic (matching Liquid)
    const priceInCents = Math.round(parseFloat(product.priceRange.minVariantPrice.amount) * 100);
    if (isNaN(priceInCents)) {
      return { metafieldDiscount: 0, isFixedAmount: false, hasMetafieldDiscount: false };
    }

    let metafieldDiscount = 0;
    let isFixedAmount = false;

    // Priority 1: Fixed Amount Off
    const rawAmount = parseFloat(product.amountOff?.value) || 0;
    if (rawAmount > 0) {
      metafieldDiscount = Math.round(rawAmount * 100);
      isFixedAmount = true;
      return { metafieldDiscount, isFixedAmount, hasMetafieldDiscount: true };
    }

    // Priority 2: Product Percentage Off
    const rawOff = parseFloat(product.percentOff?.value) || 0;
    if (rawOff > 0) {
      // Use integer arithmetic in cents: (priceInCents * rawOff) / 100
      metafieldDiscount = Math.round((priceInCents * rawOff) / 100);
      return { metafieldDiscount, isFixedAmount, hasMetafieldDiscount: true };
    }

    // Priority 3: Collection Percentage Off
    const collections = product.collections?.edges || [];
    for (const { node: collection } of collections) {
      const collectionOff = parseFloat(collection.collectionOff?.value) || 0;
      if (collectionOff > 0) {
        // Use integer arithmetic in cents: (priceInCents * collectionOff) / 100
        metafieldDiscount = Math.round((priceInCents * collectionOff) / 100);
        return { metafieldDiscount, isFixedAmount, hasMetafieldDiscount: true };
      }
    }

    return { metafieldDiscount: 0, isFixedAmount: false, hasMetafieldDiscount: false };
  }

  getCountdownTimer() {
    // Get current time in EST
    const now = new Date();
    const estTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    
    // Set end of day in EST (11:59:59 PM)
    const endOfDay = new Date(estTime);
    endOfDay.setHours(23, 59, 59, 999);
    
    // Calculate difference in milliseconds
    const diff = endOfDay - estTime;
    
    if (diff <= 0) {
      return '00:00:00';
    }
    
    // Calculate hours, minutes, seconds
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    // Format with leading zeros
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }


  startCountdown(timerElement) {
    const updateTimer = () => {
      timerElement.textContent = this.getCountdownTimer();
    };
    
    // Update every second
    setInterval(updateTimer, 1000);
  }

  render() {
    const title = this.product.title || '';
    const parts = title.split(' | ');

    const productName = parts[0];
    const series = parts.find(part =>
      /series|edition/i.test(part)
    ) || parts.find(part => part.includes('Dalstrong')) || '';
    const collections = this.product.collections?.edges?.map(edge => edge.node) || [];
    const normalizedSeries = series.trim()
    let matchingCollection = collections.find(
      col => col.title?.trim() === normalizedSeries
    );

    if (!matchingCollection) {
      matchingCollection = collections.find(
        col => normalizedSeries.toLowerCase().includes(col.title?.toLowerCase())
      );
    }
    
    const collectionSpecs = this.product.featuresBenefits?.value || matchingCollection?.metafield?.value || '';
    const collectionSpecsShort = this.product.materials?.value || matchingCollection?.mobileSpecs?.value || collectionSpecs || '';
    const variantId = this.product.variants.edges[0].node.id.split('/').pop();
    
    // Get image from metafields or fallback to featured image
    let imageUrl = '';
    const cardImageUrl = this.product.productCardImage?.reference?.image?.url;
    if (cardImageUrl) {
      imageUrl = cardImageUrl;
    } else {
      imageUrl = this.product.metafield?.reference?.image?.url || this.product.featuredImage?.url || this.product.images?.edges[0]?.node.url || '';
    }

    // Work in cents for precise integer arithmetic (matching Liquid)
    const priceInCents = Math.round(parseFloat(this.product.priceRange.minVariantPrice.amount) * 100);
    const compareAtPriceInCents = Math.round(parseFloat(this.product.compareAtPriceRange.maxVariantPrice.amount) * 100);
    const currency = this.product.priceRange.minVariantPrice.currencyCode;
    const isSoldOut = !this.product.availableForSale;
    const isPreOrder = this.product.tags.includes('pre-order');
    const isNew = this.product.tags.includes('new');
    const hasBundleSave = this.product.tags.includes('pcs_block') || this.product.tags.includes('individual_pcs');
    const isBestSeller = this.product.tags.includes('BEST_SELLER');
    const isNetflixBadge = this.product.tags.includes('Netflix_badge');
    const isBfcmDeal = this.product.tags.includes('bfcm-daily-deal');
    const isDDayDeal = this.product.tags.includes('d-day-daily-deal');
    const isBannerProd = this.product.tags.includes('banner-prod');

    let isPromoBadge = false;
    let promoBadgeHtml = '';
    if (this.promoEnabled && this.promoBadge && this.promoBadge.text && this.product.tags.includes('promotion')) {
      const now = Date.now();
      const startTs = this.promoBadge.start_date ? new Date(this.promoBadge.start_date).getTime() : null;
      const endTs = this.promoBadge.end_date ? new Date(this.promoBadge.end_date).getTime() : null;
      const inRange = (!startTs || now >= startTs) && (!endTs || now <= endTs);
      if (inRange) {
        isPromoBadge = true;
        promoBadgeHtml = `<span class="badge--new grid-product__tag--promo" style="color: ${this.promoBadge.text_color}; background-color: ${this.promoBadge.background_color}; border-color: ${this.promoBadge.background_color};">${this.promoBadge.text}</span>`;
      }
    }
    // Fall promo: 'fall-50-off' / 'fall-free-bonus' swap in their own badge with the same priority as the promotion badge; styled like the Shop by Category 2026 fall-promo badge.
    let fallPromoText = '';
    if (this.product.tags.includes('fall-50-off')) {
      fallPromoText = '50% ADD-ON';
    } else if (this.product.tags.includes('fall-free-bonus')) {
      fallPromoText = 'FREE BONUS';
    }
    if (fallPromoText) {
      isPromoBadge = true;
      promoBadgeHtml = `<span class="badge--new grid-product__tag--fall-promo">${fallPromoText}</span>`;
    }
    // Get metafield discount info (returns discount in cents)
    const { metafieldDiscount, isFixedAmount, hasMetafieldDiscount } = this.getSaleInfo(this.product);
    const isLimitedTimeDeal = hasMetafieldDiscount;
    const hasCompareAtPrice = compareAtPriceInCents > priceInCents;

    // Calculate anchor price (use compare-at-price if higher, otherwise regular price) - in cents
    // For fixed amount_off discounts the anchor is the regular price itself
    const anchorPriceInCents = (hasCompareAtPrice && !isFixedAmount) ? compareAtPriceInCents : priceInCents;
    const finalPriceInCents = hasMetafieldDiscount ? (priceInCents - metafieldDiscount) : priceInCents;

    // Calculate total savings and discount percentage from anchor price - using integer arithmetic
    const totalSavingsInCents = anchorPriceInCents - finalPriceInCents;
    const hasDiscount = totalSavingsInCents > 0;
    const discountPercentage = hasDiscount ? Math.floor((totalSavingsInCents * 100) / anchorPriceInCents) : 0;

    // Convert back to dollars for display
    const finalPrice = finalPriceInCents / 100;
    const anchorPrice = anchorPriceInCents / 100;

    // Format discount label (dollar amount for fixed discounts, percentage for others)
    let discountLabel = '';
    if (hasMetafieldDiscount && hasDiscount) {
      if (isFixedAmount) {
        // Format as currency without trailing zeros, append OFF
        discountLabel = `$${Math.floor(metafieldDiscount / 100)} OFF`;
      } else {
        discountLabel = `-${discountPercentage}%`;
      }
    }

    const productCard = document.createElement('div');
    productCard.className = 'grid__item';
    productCard.classList.add('new-card');
    productCard.innerHTML = `
      <div class="grid-product__content${isBfcmDeal ? ' bfcm-daily' : ''}${isBannerProd ? ' banner-prod' : ''}${isDDayDeal ? ' d-day-daily' : ''}">
        <div class="badge-overlay">
          ${isPromoBadge && !isSoldOut ? promoBadgeHtml : ''}
          ${isPreOrder && !isSoldOut && !isPromoBadge ? '<div class="badge--new grid-product__tag--pre-order">PRE ORDER</div>' : ''}
          ${isLimitedTimeDeal && !isPreOrder && !isSoldOut && !isPromoBadge ? '<div class="badge--new grid-product__tag--limited-time-deal">LIMITED TIME DEAL</div>' : ''}
          ${isNew && !isPreOrder && !isLimitedTimeDeal && !isSoldOut && !isNetflixBadge && !isPromoBadge ? '<div class="badge--new grid-product__tag--new">NEW</div>' : ''}
          ${hasBundleSave && !isPreOrder && !isLimitedTimeDeal && !isNew && !isSoldOut && !isNetflixBadge && !isPromoBadge ? '<div class="badge--new grid-product__tag--bundle-and-save">Bundle & Save</div>' : ''}
          ${isBestSeller && !isPreOrder && !isLimitedTimeDeal && !isNew && !hasBundleSave && !isSoldOut && !isNetflixBadge && !isPromoBadge ? '<div class="badge--new grid-product__tag--best-seller">BESTSELLER</div>' : ''}
          ${isSoldOut ? '<div class="badge--new grid-product__tag--sold-out">SOLD OUT</div>' : ''}
        </div>
        ${isNetflixBadge && !isLimitedTimeDeal ? `<img src="${this.netflixBadgeUrl}" alt="Netflix badge" loading="lazy" class="badge--netflix">` : ''}
        ${isBfcmDeal && !isSoldOut ? '<div class="badge--daily-deal">🔥 Shop today’s daily deaL</div>' : ''}
        ${isDDayDeal && !isSoldOut ? `<div class="badge--daily-deal">🔥 TODAY'S DEAL</div>` : ''}
        <div class="collection-product-wrapper">
          <div class="grid-product__image-mask">
            <a href="${this.product.onlineStoreUrl}">
              <img class="grid-product__image" src="${imageUrl}" alt="${this.product.title}" loading="lazy">
            </a>
          </div>
          <div class="collection-product__info-wrapper">
            <div class="product-container__description">
              <a href="${this.product.onlineStoreUrl}" class="grid-product__link">
                <div class="collection-product__title">
                  <div class="collection-product__collection-title">
                    <div class="collection-product__series-title">${series || ''}</div>
                    ${this.renderStars()}
                  </div>
                  <div class="collection-product__product-title">${productName}</div>
                </div>
                ${isBannerProd
                  ? `<div class="banner-prod-desc">${this.product.descriptionHtml || ''}</div>`
                  : `<div class="collection-product__collection-specs">${collectionSpecs}</div>
                     <div class="collection-product__collection-specs-short">${collectionSpecsShort}</div>`
                }
              </a>
            </div>
            <div class="collection-product__button-container">
              ${!isBannerProd ? `<div class="grid-collection-product__price${hasDiscount ? ' script-sale-price' : ''}">
                ${discountLabel ? `<span class="home-discount-percentage">${discountLabel}</span>` : ''}
                <span class="igPrice 1111" data-variant-id="${variantId}">${finalPrice.toLocaleString('en-CA', { style: 'currency', currency: currency })}</span>
                ${hasDiscount ? `<span class="home-card-discount" data-variant-id="${variantId}">${anchorPrice.toLocaleString('en-CA', { style: 'currency', currency: currency })}</span>` : ''}
                <div class="line-break"></div>
                ${hasBundleSave && !hasMetafieldDiscount ? '<div class="badge--new badge--secondary grid-product__tag--best-value">BEST VALUE</div>' : ''}
              </div>` : ''}
              <div class="collection-product__urgency-line${(hasMetafieldDiscount || isDDayDeal) ? ' timer-line' : ''}${hasBundleSave ? ' hidden-line' : ''}">
                ${(hasMetafieldDiscount || isDDayDeal) ? ((isBfcmDeal || isDDayDeal) ? `<span class="countdown-timer" data-variant-id="${variantId}">${this.getCountdownTimer()}</span> Remaining to End.` : `Limited Time Deal!`) : ''}
              </div>
            </div>
          </div>
          ${isPreOrder ? `
            <a href="${this.product.onlineStoreUrl}" class="add-to-cart btn--full">
              Pre-order
            </a>
          `
          :
          `
            <button
              class="add-to-cart btn--full" 
              data-native-pre-order-btn
              data-starting-variant="${variantId}"
              data-variant-id="${variantId}"
              ${isSoldOut ? 'disabled' : ''}
            >
              ${isSoldOut ? 'Sold Out' : 'Add to Cart'}
            </button>
          `}
        </div>
      </div>
    `;
    
    const addToCartButton = productCard.querySelector('button.add-to-cart');
    if (addToCartButton && !isSoldOut) {
      addToCartButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1
            }]
          })
        })
        .then(response => response.json())
        .then(() => {
          document.dispatchEvent(new CustomEvent('cart:build'));
        })
        .then(() => {
          document.querySelector('a.js-drawer-open-cart').click();
        })
        .catch(error => {
          console.error('Error adding item to cart:', error);
        });
      });
    }

     // Initialize countdown timer if product has bf-bundle tag and is on sale
    if (hasMetafieldDiscount && isBfcmDeal) {
      const timerElement = productCard.querySelector('.countdown-timer');
      if (timerElement) {
        this.startCountdown(timerElement);
      }
    }

    return productCard;
  }
}

class CollectionGridNew extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.isLoading = false;
    this.defaultCollectionHandle = this.dataset.defaultCollectionHandle;
    this.slides = this.querySelectorAll('.collection-grid-new__slide');
    this.defaultFilterTypes = this.dataset.filter;
    this.currentFilterTypes = {};
    this.selectedOptions = [];
    this.openFilterSections = new Set();
    this.customCardsIndexes = [0, 7, 9, 14, 16, 21, 27, 29, 32];
    this.customCards = [];
    this.customCardsData = [];
    this.activeCollectionHandle = this.defaultCollectionHandle;
    this.dalstrongLoaderElement = `
      <div class="collection-grid-new-loading-bg">
        <div class="animating-logo-wrapper" data-image-src="${ this.dataset.loaderImageUrl }"></div>
      </div>
    `;
    this.pageSize = parseInt(this.dataset.initialCount) || 48;
    this.filteredProducts = [];
    this.currentPage = 1;
    this.initialRenderDone = true;
    this.totalProductsCount = parseInt(this.dataset.totalCount) || 0;
    this.opinewRatings = this.parseOpinewRatings();
    this.netflixBadgeUrl = this.dataset.netflixBadge;
    try {
      const rawPromo = this.dataset.promoBadge;
      this.promoBadge = rawPromo ? JSON.parse(rawPromo) : null;
    } catch (e) {
      this.promoBadge = null;
    }
    this.promoEnabled = this.dataset.promoEnabled === 'true';
    this.productsFetched = false;
    this.isFetching = false;
    this.currentSortKey = 'COLLECTION_DEFAULT';
    this.currentReverse = false;
    this.abortController = null;
    this.allMinPrice = 0;
    this.allMaxPrice = 0;
    this.currentMinPrice = null;
    this.currentMaxPrice = null;
    this.hasUrlFilters = false;
  }

  get optionPicker() { return this.querySelector('.option-picker'); }
  get optionPickerBtn() { return this.querySelector('.option-picker-btn'); }
  get filterPicker() { return this.querySelector('.filter-picker'); }
  get optionPickerList() { return this.querySelector('.option-picker-list'); }

  escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  get sortPickerList() { return this.querySelector('.sort-picker-list'); }

  connectedCallback() {
    this.initSwiper();
    this.setupEventListeners();
    
    const collectionCounterElement = this.querySelector('.collection-counter');
    if (collectionCounterElement && this.totalProductsCount > 0) {
      collectionCounterElement.textContent = `(${this.totalProductsCount} results)`;
    }

    this.applyUrlFilters();
  }

  applyUrlFilters() {
    const params = new URLSearchParams(window.location.search);
    const filterParam = params.get('filter');
    if (!filterParam) return;

    this.selectedOptions = filterParam.split(',').map(v => {
      const trimmed = v.trim();
      return /^\d+$/.test(trimmed) ? `${trimmed}%` : trimmed;
    }).filter(Boolean);
    if (this.selectedOptions.length > 0) {
      this.hasUrlFilters = true;
      const productGrid = this.querySelector('.product-grid');
      if (productGrid) productGrid.innerHTML = '';
      const pagination = this.querySelector('.collection-pagination');
      if (pagination) pagination.style.display = 'none';
      const counter = this.querySelector('.collection-counter');
      if (counter) counter.textContent = '';

      this.fetchCollectionProducts(true);
    }
  }

  setupEventListeners() {
    this.slides.forEach(slide => {
      slide.addEventListener('click', (e) => {
        if (e.currentTarget.classList.contains('active')) return;

        this.slides.forEach(s => s.classList.remove('active'));
        slide.classList.add('active');
        if (this.optionPickerList) {
          this.optionPickerList.querySelectorAll('.option-picker-list-item:not(.sort-by-sidebar-item)').forEach(el => el.remove());
        }
        this.selectedOptions = [];
        this.currentMinPrice = null;
        this.currentMaxPrice = null;
        this.fetchCollectionProducts();
      });
    });
    this.addEventListener('click', (event) => {
      const { optionPickerBtn, filterPicker } = this;
      
      if (!optionPickerBtn || !filterPicker) return;

      const isClickOnButton = optionPickerBtn.contains(event.target);
      const isClickOnClose = event.target.closest('.option-picker-close') || event.target.closest('.sort-picker-close') || event.target.closest('.filter-picker-overlay');
      const isClickInsideList = event.target.closest('.option-picker-list-container');

      if (isClickOnButton) {
        if (!filterPicker.classList.contains('active')) {
          if (!this.productsFetched && !this.isFetching) {
            this.fetchCollectionProducts(false);
          }
          filterPicker.classList.add('active');
          this.optionPicker?.classList.add('is-open');
          document.body.classList.add('body-no-scroll');
          document.documentElement.classList.add('body-no-scroll');
        } else {
          filterPicker.classList.remove('active');
          this.optionPicker?.classList.remove('is-open');
          document.body.classList.remove('body-no-scroll');
          document.documentElement.classList.remove('body-no-scroll');
        }
        return;
      }
      
      if (isClickOnClose || (!isClickInsideList && filterPicker.classList.contains('active'))) {
        this.closeDrawer();
      }
    });

    this.optionPickerList?.addEventListener('click', (event) => {
      const title = event.target.closest('.option-picker-list-item-title');
      if (title) {
        const item = title.closest('.option-picker-list-item');
        const sectionTitle = title.querySelector('span')?.textContent?.trim();

        item.classList.toggle('active');

        if (sectionTitle) {
          if (item.classList.contains('active')) this.openFilterSections.add(sectionTitle);
          else this.openFilterSections.delete(sectionTitle);
        }

        this.scrollAccordionIntoView(item);
        return;
      }

      const item = event.target.closest('.sub-item');
      if (item) {
        event.preventDefault();
        event.stopPropagation();
        const checkbox = item.querySelector('.custom-checkbox');
        checkbox?.classList.toggle('checked');
        this.updateSelectedOptions(item);
      }
    });

    this.optionPickerList?.addEventListener('input', (event) => {
      const range = event.target.closest('input[type="range"]');
      if (!range) return;

      const container = range.closest('.price-range-container');
      if (!container) return;

      const minNum   = container.querySelector('.input-min');
      const maxNum   = container.querySelector('.input-max');
      const minRange = container.querySelector('.range-min');
      const maxRange = container.querySelector('.range-max');
      if (range.classList.contains('range-min')) {
        if (parseInt(minRange.value) > parseInt(maxRange.value)) minRange.value = maxRange.value;
        minNum.value = minRange.value;
      } else {
        if (parseInt(maxRange.value) < parseInt(minRange.value)) maxRange.value = minRange.value;
        maxNum.value = maxRange.value;
      }
      this.updatePriceSliderUI(container);
    });

    this.optionPickerList?.addEventListener('change', (event) => {
      const range = event.target.closest('input[type="range"]');
      if (!range) return;

      const container = range.closest('.price-range-container');
      if (!container) return;

      const minRange = container.querySelector('.range-min');
      const maxRange = container.querySelector('.range-max');

      this.currentMinPrice = parseFloat(minRange.value);
      this.currentMaxPrice = parseFloat(maxRange.value);

      if (isNaN(this.currentMinPrice)) this.currentMinPrice = this.allMinPrice;
      if (isNaN(this.currentMaxPrice)) this.currentMaxPrice = this.allMaxPrice;

      if (this.productsFetched) this.filterAndRenderProducts();
    });

    this.optionPickerList?.addEventListener('blur', (event) => {
      const num = event.target.closest('input.price-input');
      if (!num) return;

      const container = num.closest('.price-range-container');
      if (!container) return;

      const minNum   = container.querySelector('.input-min');
      const maxNum   = container.querySelector('.input-max');
      const minRange = container.querySelector('.range-min');
      const maxRange = container.querySelector('.range-max');
      let minVal = parseFloat(minNum.value);
      let maxVal = parseFloat(maxNum.value);

      if (isNaN(minVal)) minVal = parseFloat(minRange.value);
      if (isNaN(maxVal)) maxVal = parseFloat(maxRange.value);

      minVal = Math.max(this.allMinPrice, Math.min(minVal, this.allMaxPrice));
      maxVal = Math.max(this.allMinPrice, Math.min(maxVal, this.allMaxPrice));

      if (minVal > maxVal) {
        if (num.classList.contains('input-min')) maxVal = minVal;
        else minVal = maxVal;
      }

      minRange.value = String(minVal);
      maxRange.value = String(maxVal);
      minNum.value = String(minVal);
      maxNum.value = String(maxVal);
      this.updatePriceSliderUI(container);

      if (minVal === this.currentMinPrice && maxVal === this.currentMaxPrice) return;

      this.currentMinPrice = minVal;
      this.currentMaxPrice = maxVal;

      if (this.productsFetched) this.filterAndRenderProducts();
    }, true);

    this.optionPickerList?.addEventListener('keydown', (event) => {
      const num = event.target.closest('input.price-input');
      if (!num) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        num.blur();
      }
    });

    this.sortPickerList?.addEventListener('click', (event) => {
      const item = event.target.closest('.sort-picker-item');
      if (item) {
        this.updateSort(item);
        this.optionPicker?.classList.remove('is-open');
      }
    });

    this.addEventListener('click', async (e) => {
      const loadMoreBtn = e.target.closest('.collection-pagination-load-more');
      if (!loadMoreBtn || this.isFetching) return;

      if (!this.productsFetched) {
        const originalText = loadMoreBtn.textContent;
        loadMoreBtn.textContent = 'LOADING...';
        await this.fetchCollectionProducts();
        loadMoreBtn.textContent = originalText;
      }

      this.currentPage += 1;
      const start = (this.currentPage - 1) * this.pageSize;
      const totalCount = this.filteredProducts.length;
      const end = Math.min(start + this.pageSize, totalCount);
      const nextProducts = this.filteredProducts.slice(start, end);
      
      this.renderProducts(nextProducts, true); 
      this.renderPaginationControls(totalCount);
    });
  }

  scrollAccordionIntoView(sectionTitleOrItem) {
    const scroller = this.optionPickerList;
    if (!scroller) return;

    const resolveItem = () => {
      if (sectionTitleOrItem instanceof Element) return sectionTitleOrItem;

      const title = (sectionTitleOrItem || '').trim();
      if (!title) return null;

      return [...scroller.querySelectorAll('.option-picker-list-item')].find(li => {
        const t = li.querySelector('.option-picker-list-item-title span')?.textContent?.trim();
        return t === title;
      }) || null;
    };

    const doScroll = () => {
      const item = resolveItem();
      if (!item) return;

      const padding = 8;

      const stickyHeader = this.querySelector('.option-picker-list-header');
      const headerOffset = stickyHeader ? stickyHeader.offsetHeight : 0;

      scroller.scrollTo({
        top: item.offsetTop - headerOffset - padding,
        behavior: 'smooth'
      });
    };

    requestAnimationFrame(() => requestAnimationFrame(doScroll));

    clearTimeout(this._accordionScrollT);
    this._accordionScrollT = setTimeout(() => {
      requestAnimationFrame(doScroll);
    }, 320);
  }

  closeDrawer() {
    this.filterPicker?.classList.remove('active');
    this.optionPicker?.classList.remove('is-open');
    document.body.classList.remove('body-no-scroll');
    document.documentElement.classList.remove('body-no-scroll');
  }


  parseOpinewRatings() {
    const ratingsJson = document.getElementById('opinew-ratings-json');
    if (ratingsJson) {
      try {
        return JSON.parse(ratingsJson.textContent);
      } catch (e) {
        console.error('Failed to parse Opinew ratings JSON:', e);
      }
    }
    return {};
  }

  renderProducts(products, append = false) {
    let productGrid = this.querySelector('.product-grid');
    if (!productGrid) {
      productGrid = document.createElement('div');
      productGrid.className = 'product-grid';
      const optionPicker = this.querySelector('.option-picker');
      if (optionPicker) {
        optionPicker.after(productGrid);
      } else {
        this.appendChild(productGrid);
      }
    }
    
    if (!append) {
      productGrid.innerHTML = '';
    }

    const activeSlide = this.querySelector('.collection-grid-new__slide.active');
    const collectionTitle = activeSlide?.querySelector('.collection-grid-new__subtitle')?.textContent?.trim() || '';
    const collectionTitleElement = this.querySelector('.collection-title');
    const defaultCollectionTitle = collectionTitleElement?.textContent?.trim() || '';
    const collectionCounterElement = this.querySelector('.collection-counter');

    if(collectionTitleElement) {
      collectionTitleElement.textContent = defaultCollectionTitle;
    }
    
  if (collectionTitleElement && collectionTitle) {
      collectionTitleElement.textContent = collectionTitle;
    }

    productGrid.insertAdjacentHTML('beforeend', this.dalstrongLoaderElement);
    this.dalstrongLoader = productGrid.querySelector('.collection-grid-new-loading-bg');

    if (this.dalstrongLoader) {
      this.dalstrongLoader.classList.add('active');
      const logoWrapper = this.dalstrongLoader.querySelector('.animating-logo-wrapper');
      if (logoWrapper) {
        logoWrapper.insertAdjacentHTML('beforeend', `
          <img 
            src="${logoWrapper.dataset.imageSrc}"
            alt="Animated logo loader"
            width="100"
            height="88"
          />
        `);
      }
    }

    products.forEach(({ node }, index) => {
      if (this.customCardsIndexes.includes(index) && this.customCards.length > 0) {
        const customCard = this.customCards.shift();
        if (customCard) {
          productGrid.insertBefore(customCard, productGrid.children[index]);
        }
      }
      const ratingData = this.opinewRatings?.[node.handle] || { avg: 0 };

      // banner-prod promo pseudo-products are retired; never render their cards
      if (node.tags.includes('banner-prod')) return;

      const productCard = new ProductCard(node, ratingData, this.netflixBadgeUrl, this.promoBadge, this.promoEnabled);
      productGrid.appendChild(productCard.render());
    });
    
    if (collectionCounterElement) {
      collectionCounterElement.textContent = `(${this.filteredProducts.length} results)`;
    }
    setTimeout(() => {
      if (this.dalstrongLoader) {
        this.dalstrongLoader.classList.remove('active');
        const logoWrapper = this.dalstrongLoader.querySelector('.animating-logo-wrapper');
        if (logoWrapper) {
          logoWrapper.innerHTML = '';
        }
      }
    }, 500);
  }

  prepareCustomProductCardsData(cards) {
    this.customCardsData = cards || [];
    this.generateCustomCards(this.customCardsData);
  }

  generateCustomCards(cardData) {
    this.customCards = [];
    if (!cardData?.length) {
      return;
    }
    cardData.forEach((card, index) => {
      if (!card || !card.fields) {
        return;
      }
      const productField = card.fields.find(field => field.key === 'product');
      if (!productField?.reference) {
        return;
      }
      const mediaField = card.fields.find(field => field.key === 'media_file');
      let imageUrl = '';
      if (mediaField?.reference?.image?.url) {
        imageUrl = mediaField.reference.image.url;
      } else {
        imageUrl = productField.reference.featuredImage?.url || '';
      }
      const descriptionField = card.fields.find(field => field.key === 'description');
      const description = descriptionField?.value || '';
      const product = productField.reference;
      const title = product.title || '';
      const parts = title.split(' | ');
      const productName = parts[0];
      const series = parts.find(part =>
        /series|edition/i.test(part)
      ) || parts.find(part => part.includes('Dalstrong')) || '';
      const variantId = product.variants.edges[0].node.id.split('/').pop();
      const cardElement = document.createElement('div');
      const isWideCard = card.fields.find(field => field.key === 'width_2x')?.value === 'true';
      
      cardElement.className = index === 0 && isWideCard ? 'grid__item custom-product-card width-2x' : 'grid__item custom-product-card';
      let cardHTML = index === 0 && isWideCard ? 
      `
        <div class="custom-product-card-image-container">
          <img class="custom-product-card-image" src="${imageUrl}" alt="${product.title}" loading="lazy">
        </div>
        <p class="custom-product-card-description">${description.split('\n').map(line => `<span>${line}</span><br>`).join('')}</p>
        <div class="custom-product-card-content desktop">
          <div class="custom-product-card-content-image-container">
            <img class="custom-product-card-content-image" src="${product.metafield?.reference?.image?.url || ''}" alt="${product.title}">
          </div>
          <div class="custom-product-card-content-text-container">
            <p class="custom-product-card-series-title">${series || ''}</p>
            <p class="custom-product-card-title">${productName}</p>
            <button class="custom-product-card-button" data-variant-id="${variantId}">ADD TO CART</button>
          </div>
        </div>
        <div class="custom-product-card-content mobile">
          <div class="custom-product-card-content-text-container">
            <div class="custom-product-card-titles-container">
              <p class="custom-product-card-series-title">${series || ''}</p>
              <p class="custom-product-card-title">${productName}</p>
            </div>
            <button class="custom-product-card-button" data-variant-id="${variantId}">ADD TO CART</button>
          </div>
        </div>
      `
      :
      `
        <div class="custom-product-card-image-container">
          <img class="custom-product-card-image" src="${imageUrl}" alt="${product.title}" loading="lazy">
        </div>
        <div class="custom-product-card-content">
          <p class="custom-product-card-series-title">${series || ''}</p>
          <p class="custom-product-card-title">${productName}</p>
        </div>
        <div class="custom-product-card-button-wrapper">
          <button class="custom-product-card-button" data-variant-id="${variantId}">ADD TO CART</button>
        </div>
      `;
      cardElement.innerHTML = cardHTML;
      const addToCartButton = cardElement.querySelector('.custom-product-card-button');
      if (addToCartButton) {
        addToCartButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: 1
              }]
            })
          })
          .then(response => response.json())
          .then(() => {
            document.dispatchEvent(new CustomEvent('cart:build'));
          })
          .then(() => {
            document.dispatchEvent(new CustomEvent('cart:open'));
          })
          .catch(error => {
            console.error('Error adding item to cart:', error);
          });
        });
      }
      if (product.availableForSale) {
        this.customCards.push(cardElement);
      }
    });
  }

  calculateDiscount(product) {
    if (!product.priceRange) return 0;
    const priceInCents = Math.round(parseFloat(product.priceRange.minVariantPrice.amount) * 100);
    if (isNaN(priceInCents)) return 0;

    let metafieldDiscount = 0;

    const rawAmount = parseFloat(product.amountOff?.value) || 0;
    if (rawAmount > 0) {
      metafieldDiscount = Math.round(rawAmount * 100);
    } else {
      const rawOff = parseFloat(product.percentOff?.value) || 0;
      if (rawOff > 0) {
        metafieldDiscount = Math.round((priceInCents * rawOff) / 100);
      } else {
        const collections = product.collections?.edges || [];
        for (const { node: collection } of collections) {
          const collectionOff = parseFloat(collection.collectionOff?.value) || 0;
          if (collectionOff > 0) {
            metafieldDiscount = Math.round((priceInCents * collectionOff) / 100);
            break;
          }
        }
      }
    }

    const hasMetafieldDiscount = metafieldDiscount > 0;
    if (!hasMetafieldDiscount) return 0;

    const compareAtPriceInCents = product.compareAtPriceRange?.maxVariantPrice?.amount
      ? Math.round(parseFloat(product.compareAtPriceRange.maxVariantPrice.amount) * 100)
      : 0;

    const hasCompareAtPrice = compareAtPriceInCents > priceInCents;

    const anchorPriceInCents = hasCompareAtPrice ? compareAtPriceInCents : priceInCents;
    const finalPriceInCents = priceInCents - metafieldDiscount;

    const totalSavingsInCents = anchorPriceInCents - finalPriceInCents;
    return totalSavingsInCents > 0 ? Math.floor((totalSavingsInCents * 100) / anchorPriceInCents) : 0;
  }

  getFinalPrice(product) {
    if (!product?.priceRange?.minVariantPrice?.amount) return 0;
    const priceInCents = Math.round(parseFloat(product.priceRange.minVariantPrice.amount) * 100);
    if (isNaN(priceInCents)) return 0;

    let discountInCents = 0;
    const rawAmount = parseFloat(product.amountOff?.value) || 0;
    if (rawAmount > 0) {
      discountInCents = Math.round(rawAmount * 100);
    } else {
      const rawOff = parseFloat(product.percentOff?.value) || 0;
      if (rawOff > 0) {
        discountInCents = Math.round((priceInCents * rawOff) / 100);
      } else {
        const collections = product.collections?.edges || [];
        for (const { node: collection } of collections) {
          const collectionOff = parseFloat(collection.collectionOff?.value) || 0;
          if (collectionOff > 0) {
            discountInCents = Math.round((priceInCents * collectionOff) / 100);
            break;
          }
        }
      }
    }

    const finalInCents = priceInCents - discountInCents;
    return finalInCents / 100;
  }

  async fetchCollectionProducts(shouldRender = true) {
    if (this.isFetching && this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    console.warn('Fetching collection products...');
    this.isFetching = true;
    this.renderFilterTypes();

    try {
      if (this.slides) {
        this.slides.forEach(slide => {
          if (slide.classList.contains('active')) {
            this.activeCollectionHandle = slide.dataset.subcollectionHandle;
          }
        });
      }

      const query = `
        query getCollectionWithProducts($handle: String!, $cursor: String, $sortKey: ProductCollectionSortKeys, $reverse: Boolean) {
          collection(handle: $handle) {
            id
            title
            description
            metafield(namespace: "custom", key: "filter_types") {
              value
              references(first: 50) {
                nodes {
                  ... on Metaobject {
                    fields {
                      key
                      value
                    }
                  }
                }
              }
            }
            customProductCards: metafield(namespace: "custom", key: "custom_card_products") {
              value
              references(first: 50) {
                nodes {
                  ... on Metaobject {
                    id
                    type
                    fields {
                      key
                      value
                      reference {
                        ... on Product {
                          id
                          title
                          handle
                          onlineStoreUrl
                          availableForSale
                          featuredImage {
                            url
                            altText
                          }
                          metafield(namespace: "img", key: "card_image") {
                            value
                            reference {
                              ... on MediaImage {
                                image {
                                  url
                                  altText
                                }
                              }
                            }
                          }
                          variants(first: 1) {
                            edges {
                              node {
                                id
                              }
                            }
                          }
                          amountOff: metafield(namespace: "custom", key: "amount_off") {
                            value
                          }
                          percentOff: metafield(namespace: "custom", key: "off") {
                            value
                          }
                          priceRange {
                            minVariantPrice {
                              amount
                            }
                          }
                          compareAtPriceRange {
                            maxVariantPrice {
                              amount
                            }
                          }
                          collections(first: 100) {
                            edges {
                              node {
                                title
                                handle
                                collectionOff: metafield(namespace: "custom", key: "off") {
                                  value
                                }
                              }
                            }
                          }
                          tags
                        }
                        ... on MediaImage {
                          image {
                            url
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            products(first: 250, after: $cursor, sortKey: $sortKey, reverse: $reverse) {
              pageInfo {
                hasNextPage
                endCursor
              }
              edges {
                node {
                  id
                  title
                  handle
                  onlineStoreUrl
                  availableForSale
                  featuredImage {
                    url
                    altText
                  }
                  metafield(namespace: "img", key: "card_image") {
                    value
                    reference {
                      ... on MediaImage {
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                  productCardImage: metafield(namespace: "custom", key: "product_card_image") {
                    reference {
                      ... on MediaImage {
                        image {
                          url
                        }
                      }
                    }
                  }
                  featuresBenefits: metafield(namespace: "custom", key: "features_benefits") {
                    value
                  }
                  materials: metafield(namespace: "custom", key: "materials") {
                    value
                  }
                  amountOff: metafield(namespace: "custom", key: "amount_off") {
                    value
                  }
                  percentOff: metafield(namespace: "custom", key: "off") {
                    value
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  compareAtPriceRange {
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  variants(first: 1) {
                    edges {
                      node {
                        id
                        availableForSale
                        price {
                          amount
                          currencyCode
                        }
                        compareAtPrice {
                          amount
                          currencyCode
                        }
                      }
                    }
                  }
                  tags
                  descriptionHtml
                  collections(first: 100) {
                    edges {
                      node {
                        title
                        handle
                        metafield(namespace: "custom", key: "cards_specifications") {
                          value
                        }
                        mobileSpecs: metafield(namespace: "custom", key: "cards_specifications_short") {
                          value
                        }
                        collectionOff: metafield(namespace: "custom", key: "off") {
                          value
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      let allProductEdges = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const variables = {
          handle: this.activeCollectionHandle,
          cursor,
          sortKey: this.currentSortKey,
          reverse: this.currentReverse
        };
  
        const res = await fetch('https://dalstrong.ca/api/2024-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': 'a7e339ad22be0bfa26296383c45a76c9'
          },
          body: JSON.stringify({ query, variables }),
          signal
        });

        const json = await res.json();
        if (json.errors) {
          console.error('GraphQL Errors:', json.errors);
          break;
        }

        const collection = json.data.collection;
        const productData = collection.products;

        allProductEdges.push(...productData.edges);
        hasNextPage = productData.pageInfo.hasNextPage;
        cursor = productData.pageInfo.endCursor;

        if (collection.metafield?.references?.nodes) {
          this.configureFilterTypes(allProductEdges, collection.metafield);
          this.renderFilterTypes();
        } else if (this.defaultFilterTypes) {
          try {
            const filterTypesData = JSON.parse(this.defaultFilterTypes);
            const filterTypes = {
              references: {
                nodes: filterTypesData.map(type => ({
                  fields: [
                    { key: 'title', value: type.title },
                    { key: 'options', value: JSON.stringify(type.options) }
                  ]
                }))
              }
            };
            this.configureFilterTypes(allProductEdges, filterTypes);
            this.renderFilterTypes();
          } catch (e) {
            console.error('Error parsing filter types:', e);
            this.optionPickerList.querySelectorAll('.option-picker-list-item:not(.sort-by-sidebar-item)').forEach(el => el.remove());
          }
        } else {
          this.optionPickerList.querySelectorAll('.option-picker-list-item:not(.sort-by-sidebar-item)').forEach(el => el.remove());
        }

        if (collection.customProductCards?.references?.nodes) {
          this.prepareCustomProductCardsData(collection.customProductCards.references.nodes);
        }
      }

      if (signal.aborted) return;

      sessionStorage.setItem('allProducts', JSON.stringify(allProductEdges));
      this.allProducts = allProductEdges;
      this.productsFetched = true;

      if (this.hasUrlFilters && this.selectedOptions.length > 0) {
        const allOptions = Object.values(this.currentFilterTypes).flat();
        this.selectedOptions = this.selectedOptions.map(selected => {
          return allOptions.find(opt => opt.toLowerCase() === selected.toLowerCase()) || selected;
        });
      }

      if (shouldRender) {
        this.filterAndRenderProducts();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
        return;
      }
      console.error('Error fetching collection products:', error);
    } finally {
      if (!signal.aborted) {
        this.isFetching = false;
        this.renderFilterTypes();
      }
    }
  }

  configureFilterTypes(collection, metafield) {
    this.currentFilterTypes = {};
    this.filterOptionCounts = {};
    
    const dealOptions = ['On Sale', 'New Arrivals', 'In Stock', 'Best Sellers'];
    const availableDeals = new Set();
    
    collection.forEach(({ node: product }) => {
      const discount = this.calculateDiscount(product);
      const isNew = product.tags.some(tag => {
        const t = tag.toLowerCase();
        return t === 'new';
      });
      const isInStock = product.availableForSale;
      const isBestSeller = product.tags.some(tag => tag.toLowerCase() === 'best_seller');

      if (discount > 0) {
        const opt = 'On Sale';
        availableDeals.add(opt);
        this.filterOptionCounts[opt] = (this.filterOptionCounts[opt] || 0) + 1;
      }
      if (isNew) {
        const opt = 'New Arrivals';
        availableDeals.add(opt);
        this.filterOptionCounts[opt] = (this.filterOptionCounts[opt] || 0) + 1;
      }
      if (isInStock) {
        const opt = 'In Stock';
        availableDeals.add(opt);
        this.filterOptionCounts[opt] = (this.filterOptionCounts[opt] || 0) + 1;
      }
      if (isBestSeller) {
        const opt = 'Best Sellers';
        availableDeals.add(opt);
        this.filterOptionCounts[opt] = (this.filterOptionCounts[opt] || 0) + 1;
      }
    });

    if (availableDeals.size > 0) {
      this.currentFilterTypes['DEALS & STATUS'] = dealOptions.filter(opt => availableDeals.has(opt));
    }

    const filterTypeMetaobjects = metafield.references.nodes;

    this.discountTiers = [];
    filterTypeMetaobjects.forEach(metaobject => {
      const titleField = metaobject.fields.find(field => field.key === 'title');
      const optionsField = metaobject.fields.find(field => field.key === 'options');
      if (titleField && optionsField && titleField.value === 'Discount') {
        this.discountTiers = JSON.parse(optionsField.value)
          .map(o => parseInt(o))
          .filter(n => !isNaN(n) && n > 0)
          .sort((a, b) => b - a);
      }
    });

    collection.forEach(product => {
      filterTypeMetaobjects.forEach(metaobject => {
        const titleField = metaobject.fields.find(field => field.key === 'title');
        const optionsField = metaobject.fields.find(field => field.key === 'options');

        if (titleField && optionsField) {
          const title = titleField.value;
          const options = JSON.parse(optionsField.value);

          if (title === 'Discount') {
            const discount = this.calculateDiscount(product.node);
            if (discount > 0 && this.discountTiers.includes(discount)) {
              const bucket = `${discount}%`;
              if (!Array.isArray(this.currentFilterTypes[title])) {
                this.currentFilterTypes[title] = [];
              }
              if (!this.currentFilterTypes[title].includes(bucket)) {
                this.currentFilterTypes[title].push(bucket);
              }
              this.filterOptionCounts[bucket] = (this.filterOptionCounts[bucket] || 0) + 1;
            }
          } else {
            options.forEach(option => {
              if (product.node.tags.includes(option)) {
                if (!Array.isArray(this.currentFilterTypes[title])) {
                  this.currentFilterTypes[title] = [];
                }
                if (!this.currentFilterTypes[title].includes(option)) {
                  this.currentFilterTypes[title].push(option);
                }
                this.filterOptionCounts[option] = (this.filterOptionCounts[option] || 0) + 1;
              }
            });
          }
        }
      });
    });

    if (this.currentFilterTypes["Blade Length"]) {
      this.currentFilterTypes["Blade Length"].sort((a, b) => {
        return parseFloat(a) - parseFloat(b);
      });
    }
    if (this.currentFilterTypes["Cutting Board Size"]) {
      this.currentFilterTypes["Cutting Board Size"].sort((a, b) => {
        return parseFloat(a) - parseFloat(b);
      });
    }
    if (this.currentFilterTypes["Honing Rod Length"]) {
      this.currentFilterTypes["Honing Rod Length"].sort((a, b) => {
        return parseFloat(a) - parseFloat(b);
      });
    }
    if (this.currentFilterTypes["Grit"]) {
      this.currentFilterTypes["Grit"].sort((a, b) => {
        return parseFloat(a) - parseFloat(b);
      });
    }
    if (this.currentFilterTypes["Discount"]) {
      this.currentFilterTypes["Discount"].sort((a, b) => {
        return parseInt(a) - parseInt(b);
      });
    }

    // price range calculation
    let min = Infinity;
    let max = -Infinity;
    collection.forEach(({ node: product }) => {
      const price = this.getFinalPrice(product);
      if (price < min) min = price;
      if (price > max) max = price;
    });
    this.allMinPrice = min === Infinity ? 0 : Math.floor(min);
    this.allMaxPrice = max === -Infinity ? 0 : Math.ceil(max);

    if (this.currentMinPrice === null) this.currentMinPrice = this.allMinPrice;
    if (this.currentMaxPrice === null) this.currentMaxPrice = this.allMaxPrice;
  }

  renderFilterTypes() {
    if (!this.optionPickerList) return;

    if (this.isFetching && Object.keys(this.currentFilterTypes).length === 0) {
      if (!this.optionPickerList.querySelector('.loading-text')) {
        this.optionPickerList.insertAdjacentHTML(
          'beforeend',
          '<p class="loading-text" style="padding: 20px;">Loading...</p>'
        );
      }
      this.optionPickerBtn?.classList.remove('hide');
      return;
    }

    this.optionPickerList.querySelector('.loading-text')?.remove();

    const sortByItem = this.optionPickerList.querySelector('.sort-by-sidebar-item');

    this.optionPickerList.innerHTML = '';

    if (sortByItem) this.optionPickerList.appendChild(sortByItem);

    const priceItem = document.createElement('li');
    priceItem.classList.add('option-picker-list-item');
    if (this.openFilterSections.has('Price Range')) {
      priceItem.classList.add('active');
    }

    priceItem.innerHTML = `
      <p class="option-picker-list-item-title">
        <span>Price Range</span>
        <span class="accordion-icon">
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L5 5L9 1" stroke="black" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </p>
      <div class="option-picker-list-sub price-range-container">
        <div class="price-display-wrapper">
          <span class="display-min">$${Math.floor(this.allMinPrice)}</span>
          <span class="display-separator">—</span>
          <span class="display-max">$${Math.ceil(this.allMaxPrice)}</span>
        </div>
        <div class="price-slider-container">
          <div class="price-slider-track"></div>
          <input type="range" class="range-min" min="${this.allMinPrice}" max="${this.allMaxPrice}" value="${this.currentMinPrice !== null ? this.currentMinPrice : this.allMinPrice}" step="1">
          <input type="range" class="range-max" min="${this.allMinPrice}" max="${this.allMaxPrice}" value="${this.currentMaxPrice !== null ? this.currentMaxPrice : this.allMaxPrice}" step="1">
        </div>
        <div class="price-input-wrapper">
          <div class="price-field">
            <label>Min Price</label>
            <div class="input-with-prefix">
              <span>$</span>
              <input type="number" class="price-input input-min" value="${this.currentMinPrice !== null ? this.currentMinPrice : this.allMinPrice}" min="${this.allMinPrice}" max="${this.allMaxPrice}">
            </div>
          </div>
          <div class="price-field">
            <label>Max Price</label>
            <div class="input-with-prefix">
              <span>$</span>
              <input type="number" class="price-input input-max" value="${this.currentMaxPrice !== null ? this.currentMaxPrice : this.allMaxPrice}" min="${this.allMinPrice}" max="${this.allMaxPrice}">
            </div>
          </div>
        </div>
      </div>
    `;

    this.optionPickerList.appendChild(priceItem);
    this.updatePriceSliderUI(priceItem);

    const filterTypesPrefixes = JSON.parse(this.dataset.filterTypesPrefixes || '{}');
    const filterTypesEntries = Object.entries(this.currentFilterTypes);

    filterTypesEntries.forEach(([title, options]) => {
      if (title === 'Price Range') return;

      const typeItem = document.createElement('li');
      typeItem.classList.add('option-picker-list-item');

      if (this.openFilterSections.has(title)) {
        typeItem.classList.add('active');
      }

      const prefix = filterTypesPrefixes[title] || '';

      typeItem.innerHTML = `
        <p class="option-picker-list-item-title">
          <span>${title}</span>
          <span class="accordion-icon">
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L5 5L9 1" stroke="black" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </p>
        <ul class="option-picker-list-sub">
          ${options.map(option => {
            const displayOption = prefix && option.startsWith(prefix)
              ? option.replace(prefix, '')
              : option;

            const isChecked = this.selectedOptions.includes(option);

            return `
              <li class="sub-item">
                <div class="sub-item-checkbox">
                  <div class="custom-checkbox${isChecked ? ' checked' : ''}"></div>
                  <p data-option-tag='${option}'>${displayOption}</p>
                </div>
                <div class="sub-item-count">
                  <span>(${this.filterOptionCounts[option] || 0})</span>
                </div>
              </li>
            `;
          }).join('')}
        </ul>
      `;

      this.optionPickerList.appendChild(typeItem);
    });

    this.optionPickerBtn?.classList.remove('hide');
  }

  updatePriceSliderUI(container) {
    const minRange = container.querySelector('.range-min');
    const maxRange = container.querySelector('.range-max');
    const track = container.querySelector('.price-slider-track');
    
    if (!minRange || !maxRange || !track) return;

    const min = parseFloat(minRange.min);
    const max = parseFloat(minRange.max);
    const v1 = parseFloat(minRange.value);
    const v2 = parseFloat(maxRange.value);

    const left = ((v1 - min) / (max - min)) * 100;
    const right = 100 - (((v2 - min) / (max - min)) * 100);

    track.style.left = left + '%';
    track.style.right = right + '%';
  }

  filterAndRenderProducts() {
    if (this.dalstrongLoader) {
      this.dalstrongLoader.classList.add('active');
      const logoWrapper = this.dalstrongLoader.querySelector('.animating-logo-wrapper');
      if (logoWrapper) {
        logoWrapper.insertAdjacentHTML('beforeend', `
          <img 
            src="${logoWrapper.dataset.imageSrc}"
            alt="Animated logo loader"
            width="100"
            height="88"
          />
        `);
      }
    }

    const allProducts = this.allProducts || JSON.parse(sessionStorage.getItem('allProducts')) || [];
    let filteredProducts = allProducts;
    let filteredCustomCardsData = this.customCardsData;

    const groupedOptions = {};
    if (this.selectedOptions.length > 0) {
      this.selectedOptions.forEach(option => {
        Object.entries(this.currentFilterTypes).forEach(([type, options]) => {
          if (options.includes(option)) {
            if (!groupedOptions[type]) {
              groupedOptions[type] = [];
            }
            groupedOptions[type].push(option);
          }
        });
      });
    }

    const filterFn = (node) => {
      const optionMatch = Object.entries(groupedOptions).every(([type, options]) => {
        if (type === 'DEALS & STATUS') {
          return options.every(option => {
            if (option === 'On Sale') return this.calculateDiscount(node) > 0;
            if (option === 'New Arrivals') return node.tags.some(tag => tag.toLowerCase() === 'new');
            if (option === 'In Stock') return node.availableForSale;
            if (option === 'Best Sellers') return node.tags.some(tag => tag.toLowerCase() === 'best_seller');
            return false;
          });
        }
        if (type === 'Discount') {
          const discount = this.calculateDiscount(node);
          return options.some(option => parseInt(option) === discount);
        }
        return options.some(option => node.tags.includes(option));
      });

      if (!optionMatch) return false;

      const price = this.getFinalPrice(node);
      const min = this.currentMinPrice !== null ? this.currentMinPrice : -Infinity;
      const max = this.currentMaxPrice !== null ? this.currentMaxPrice : Infinity;
      
      return price >= min && price <= max;
    };

    let list = allProducts.filter(({ node }) => filterFn(node));

    if (this.currentSortKey === 'PRICE') {
      list.sort((a, b) => {
        const priceA = this.getFinalPrice(a.node);
        const priceB = this.getFinalPrice(b.node);
        return this.currentReverse ? priceB - priceA : priceA - priceB;
      });
    }

    const inStock = [];
    const outOfStock = [];
    list.forEach(item => {
      (item.node.availableForSale ? inStock : outOfStock).push(item);
    });

    filteredProducts = inStock.concat(outOfStock);

    filteredCustomCardsData = this.customCardsData.filter(card => {
      const product = card.fields.find(field => field.key === 'product').reference;
      return filterFn(product);
    });

    this.generateCustomCards(filteredCustomCardsData);
    this.filteredProducts = filteredProducts;

    const dealsCountBase = filteredProducts;
    const tagCountBase = allProducts.filter(({ node }) => {
      const dealsOptions = groupedOptions['DEALS & STATUS'];
      if (dealsOptions && dealsOptions.length > 0) {
        const dealsMatch = dealsOptions.every(option => {
          if (option === 'On Sale') return this.calculateDiscount(node) > 0;
          if (option === 'New Arrivals') return node.tags.some(tag => tag.toLowerCase() === 'new');
          if (option === 'In Stock') return node.availableForSale;
          if (option === 'Best Sellers') return node.tags.some(tag => tag.toLowerCase() === 'best_seller');
          return false;
        });
        if (!dealsMatch) return false;
      }
      const price = this.getFinalPrice(node);
      const min = this.currentMinPrice !== null ? this.currentMinPrice : -Infinity;
      const max = this.currentMaxPrice !== null ? this.currentMaxPrice : Infinity;
      return price >= min && price <= max;
    });
    this.recalcCountsFromEdges(dealsCountBase, tagCountBase);
    this.renderFilterTypes();
    this.currentPage = 1;
    const paginatedProducts = this.filteredProducts.slice(0, this.pageSize);
    this.renderProducts(paginatedProducts, false);
    this.renderPaginationControls(this.filteredProducts.length);
    this.renderFilterChips();
  }

  renderFilterChips() {
    let chipsContainer = this.querySelector('.filter-chips');
    if (this.selectedOptions.length === 0) {
      if (chipsContainer) chipsContainer.remove();
      return;
    }

    if (!chipsContainer) {
      chipsContainer = document.createElement('div');
      chipsContainer.className = 'filter-chips';
      const optionPicker = this.querySelector('.option-picker');
      if (optionPicker) {
        optionPicker.after(chipsContainer);
      }
    }

    const filterTypesPrefixes = JSON.parse(this.dataset.filterTypesPrefixes || '{}');

    const chipsHTML = this.selectedOptions.map(option => {
      let displayName = option;
      Object.entries(this.currentFilterTypes).forEach(([type, options]) => {
        if (options.includes(option)) {
          const prefix = filterTypesPrefixes[type] || '';
          if (prefix && option.startsWith(prefix)) {
            displayName = option.replace(prefix, '');
          }
        }
      });
      return `<button class="filter-chip" data-option="${this.escapeAttr(option)}">
        <span>${displayName}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>`;
    }).join('');

    const clearLabel = this.hasUrlFilters ? 'View All' : 'Clear All';
    chipsContainer.innerHTML = chipsHTML + `<button class="filter-chip filter-chip--clear">${clearLabel}</button>`;

    chipsContainer.querySelectorAll('.filter-chip:not(.filter-chip--clear)').forEach(chip => {
      chip.addEventListener('click', () => {
        const option = chip.dataset.option;
        this.selectedOptions = this.selectedOptions.filter(o => o !== option);
        this.optionPickerList?.querySelectorAll('.sub-item').forEach(item => {
          if (item.querySelector('p')?.dataset.optionTag === option) {
            item.querySelector('.custom-checkbox')?.classList.remove('checked');
          }
        });
        if (this.productsFetched) {
          this.filterAndRenderProducts();
        }
      });
    });

    chipsContainer.querySelector('.filter-chip--clear')?.addEventListener('click', () => {
      this.selectedOptions = [];
      this.hasUrlFilters = false;
      history.replaceState(null, '', window.location.pathname);
      this.optionPickerList?.querySelectorAll('.custom-checkbox').forEach(cb => cb.classList.remove('checked'));
      if (this.productsFetched) {
        this.filterAndRenderProducts();
      }
    });
  }

  updateSelectedOptions(item) {
    const option = item.querySelector('p').dataset.optionTag;
    if (this.selectedOptions.includes(option)) {
      this.selectedOptions = this.selectedOptions.filter(o => o !== option);
    } else {
      this.selectedOptions.push(option);
    }

    if (this.productsFetched) {
      this.filterAndRenderProducts();
    } else {
      this.fetchCollectionProducts(true);
    }
  }

  updateSort(item) {
    const sortKey = item.dataset.sortKey;
    const reverse = item.dataset.reverse === 'true';

    if (this.currentSortKey === sortKey && this.currentReverse === reverse) return;

    this.currentSortKey = sortKey;
    this.currentReverse = reverse;

    this.querySelectorAll('.sort-picker-item').forEach(el => el.classList.remove('active'));
    this.querySelectorAll('.sort-picker-item .custom-radio').forEach(el => el.classList.remove('active'));
    item.classList.add('active');
    const radio = item.querySelector('.custom-radio');
    if (radio) radio.classList.add('active');

    this.closeDrawer();

    if (sortKey === 'PRICE') {
      if (this.productsFetched) {
        this.filterAndRenderProducts();
      } else {
        this.currentPage = 1;
        this.fetchCollectionProducts(true);
      }
    } else {
      this.productsFetched = false;
      this.allProducts = null;
      sessionStorage.removeItem('allProducts');
      this.currentPage = 1;
      this.fetchCollectionProducts(true);
    }
  }

  recalcCountsFromEdges(dealsEdges, tagEdges) {
    this.filterOptionCounts = {};

    const inc = (key) => {
      this.filterOptionCounts[key] = (this.filterOptionCounts[key] || 0) + 1;
    };

    dealsEdges.forEach(({ node }) => {
      if (!node) return;
      if (this.calculateDiscount(node) > 0) inc('On Sale');
      if (node.tags?.some(t => (t || '').toLowerCase() === 'new')) inc('New Arrivals');
      if (node.availableForSale) inc('In Stock');
      if (node.tags?.some(t => (t || '').toLowerCase() === 'best_seller')) inc('Best Sellers');
    });

    const base = tagEdges || dealsEdges;
    base.forEach(({ node }) => {
      if (!node) return;
      if (this.discountTiers?.length > 0) {
        const discount = this.calculateDiscount(node);
        if (discount > 0 && this.discountTiers.includes(discount)) {
          inc(`${discount}%`);
        }
      }
      (node.tags || []).forEach(tag => inc(tag));
    });
  }

  initSwiper() {
    const swiperElement = this.querySelector('.swiper');
    if (!swiperElement) return;

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: 'auto',
      spaceBetween: 30,
      grabCursor: 'true',
      mousewheel: window.innerWidth > 768 ? { forceToAxis: true, sensitivity: 1, releaseOnEdges: true } : false,
      breakpoints: {
        320: {
          spaceBetween: 20
        },
        769: {
          spaceBetween: 30
        }
      }
    });
  }

  renderPaginationControls(totalProducts) {
    let paginationContainer = this.querySelector('.collection-pagination');
    if (!paginationContainer) {
      paginationContainer = document.createElement('div');
      paginationContainer.className = 'collection-pagination';
      this.appendChild(paginationContainer);
    }
    paginationContainer.innerHTML = '';

    const displayedCount = Math.min(this.currentPage * this.pageSize, totalProducts);
    const startIndex = totalProducts > 0 ? 1 : 0;
    const endIndex = displayedCount;
    if (totalProducts === 0) {
      paginationContainer.style.display = 'none';
      return;
    }
    paginationContainer.style.display = '';

    const rangeText = document.createElement('div');
    rangeText.className = 'collection-pagination-range';
    rangeText.textContent = `${startIndex} - ${endIndex} of ${totalProducts} PRODUCTS`;
    paginationContainer.appendChild(rangeText);

    if (endIndex < totalProducts) {
      const loadMoreBtn = document.createElement('button');
      loadMoreBtn.className = 'collection-pagination-load-more';
      loadMoreBtn.textContent = 'LOAD MORE';
      paginationContainer.appendChild(loadMoreBtn);
    }
  }
}

customElements.define('collection-grid-new', CollectionGridNew);